// src/api/axiosInstance.ts
import axios, { AxiosResponse, AxiosRequestConfig } from "axios";
import { snackbarStore, type AlertType } from "src/store/snackbarStore";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "src/utils/tokenCookie";
export const BASE_URL = process.env.REACT_APP_OMS_SERVER_API || "http://localhost:8080";

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  // withCredentials: true,
});

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

// ✅ 요청별 스낵바 옵션 타입
type SnackbarMeta = {
  /** 성공 토스트: true=항상, false=절대, undefined=기본룰(뮤테이션만) */
  success?: boolean;
  /** 에러 토스트: false면 끔 (기본 ON) */
  error?: boolean;
  /** 성공 메시지 override */
  successMessage?: string;
  /** 에러 메시지 override */
  errorMessage?: string;
  /** 성공 severity override */
  severitySuccess?: AlertType;
};

// ✅ axios config에 snackbar 옵션을 달 수 있게 확장
declare module "axios" {
  export interface AxiosRequestConfig {
    snackbar?: SnackbarMeta;
  }
}

// =======================
// 요청 인터셉터
// =======================
axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    // axios v1 headers는 AxiosHeaders일 수 있음
    if (config.headers && typeof (config.headers as any).set === "function") {
      (config.headers as any).set("Authorization-OMS", `Bearer ${token}`);
    } else {
      config.headers = config.headers ?? {};
      (config.headers as any)["Authorization-OMS"] = `Bearer ${token}`;
    }
  }

  // 디버깅용 로그
  // console.log("Request headers:", config.headers);
  // console.log("Request data type:", (config as any).data?.constructor?.name);

  return config;
});

// =======================
// refresh 동시성 제어
// =======================
let isRefreshing = false;
let waiters: Array<(token: string) => void> = [];

const waitForRefresh = () =>
  new Promise<string>((resolve) => {
    waiters.push(resolve);
  });

const notifyWaiters = (token: string) => {
  waiters.forEach((r) => r(token));
  waiters = [];
};

async function refreshAccessToken(): Promise<string> {
  const rt = getRefreshToken();
  if (!rt) throw new Error("NO_REFRESH_TOKEN");

  const res = await axios.post(
    `${BASE_URL}/v1/admin/auth/refresh`,
    { refreshToken: rt },
    { headers: { "Content-Type": "application/json" } }
  );

  const { accessToken, refreshToken } = res.data?.data || {};
  if (!accessToken) throw new Error("INVALID_REFRESH_RESPONSE");

  setTokens(accessToken, refreshToken);
  return accessToken;
}

// ✅ 성공 토스트 판단 함수
function shouldToastSuccess(config: AxiosRequestConfig) {
  const method = String(config.method || "").toLowerCase();
  const isMutation = ["post", "put", "patch", "delete"].includes(method);

  const opt = config.snackbar;
  if (opt?.success === true) return true;
  if (opt?.success === false) return false;

  // 기본 룰: mutation만 성공 토스트
  return isMutation;
}

// ✅ 성공 메시지 기본값 (원하면 더 늘려도 됨)
function getDefaultSuccessMessage(config: AxiosRequestConfig) {
  const method = String(config.method || "").toLowerCase();
  if (method === "post") return "등록되었습니다.";
  if (method === "put" || method === "patch") return "수정되었습니다.";
  if (method === "delete") return "삭제되었습니다.";
  return "처리되었습니다.";
}

// =======================
// 응답 인터셉터
// =======================
axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const cfg = res.config;

    // ✅ 성공인데도 body에 statusCode로 실패 내려오는 케이스 방어(너네 API 스타일)
    // - statusCode: 1 성공, 0 성공, undefined(무시) => ok
    // - 그 외는 실패로 보고 error 토스트로 처리
    const statusCode = (res.data as any)?.statusCode ?? (res.data as any)?.status_code;
    const ok = statusCode === undefined || statusCode === 1;

    if (!ok) {
      const msg =
        cfg.snackbar?.errorMessage || (res.data as any)?.message || "요청 중 오류가 발생했습니다.";

      // body 실패는 에러처럼 토스트 + reject
      if (cfg.snackbar?.error !== false) {
        snackbarStore.getState().addSnackbar({ message: msg, severity: "error" });
      }
      return Promise.reject(new Error(msg));
    }

    // ✅ 성공 토스트 (기본: mutation만)
    if (shouldToastSuccess(cfg)) {
      const msg =
        cfg.snackbar?.successMessage || (res.data as any)?.message || getDefaultSuccessMessage(cfg);

      snackbarStore.getState().addSnackbar({
        message: msg,
        severity: cfg.snackbar?.severitySuccess ?? "success",
      });
    }

    return res;
  },
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config as RetryConfig;

    const reqUrl = String(originalRequest?.url ?? "");
    const isBrowser = typeof window !== "undefined";
    const pathname = isBrowser ? window.location.pathname : "";

    const isRefreshApi = reqUrl.includes("/auth/refresh");
    const isLoginApi = reqUrl.includes("/auth/google/token");
    const isLoginPage = pathname.startsWith("/login");

    // =======================
    // ❌ refresh API에서 401 → 즉시 로그아웃
    // =======================

    if (status === 500 && isRefreshApi) {
      clearTokens();
      // 이미 /login에 있으면 리다이렉트하지 않음 (무한 루프 방지)
      if (isBrowser && !isLoginPage) window.location.replace("/login");
      return Promise.reject(error);
    }

    if (status === 401 && isRefreshApi) {
      clearTokens();
      // 이미 /login에 있으면 리다이렉트하지 않음 (무한 루프 방지)
      if (isBrowser && !isLoginPage) window.location.replace("/login");
      return Promise.reject(error);
    }

    // =======================
    // ❌ 로그인/콜백 중 401 → 리다이렉트 금지
    // =======================
    if (status === 401 && (isLoginApi || isLoginPage)) {
      return Promise.reject(error);
    }

    // =======================
    // ✅ 일반 API 401 → refresh 시도
    // =======================
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (isRefreshing) {
          const newToken = await waitForRefresh();
          originalRequest.headers = originalRequest.headers ?? {};
          (originalRequest.headers as any)["Authorization-OMS"] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }

        isRefreshing = true;
        const newToken = await refreshAccessToken();
        notifyWaiters(newToken);

        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as any)["Authorization-OMS"] = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (e) {
        clearTokens();
        // 이미 /login에 있으면 리다이렉트하지 않음 (무한 루프 방지)
        if (isBrowser && !isLoginPage) window.location.replace("/login");
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    // =======================
    // 공통 에러 스낵바 (기본 ON, 요청에서 error:false로 끌 수 있음)
    // =======================
    const opt = originalRequest?.snackbar;
    if (opt?.error !== false) {
      snackbarStore.getState().addSnackbar({
        message:
          opt?.errorMessage ||
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "요청 중 오류가 발생했습니다.",
        severity: "error",
      });
    }

    return Promise.reject(error);
  }
);
